import { Component, Vue } from "vue-property-decorator";
import ResourceReturnModel from "@/models/ResourceReturnModel";
import Geolocation from "@/services/Geolocation";
import OnboardApiError from "@/errors/OnboardApiError";
import { hasData } from "@/utils/hasData";

@Component
export class GeolocalizationDialog extends Vue {
  private geolocalizationPermissionStatus: PermissionStatus;
  onChangeGeolocalizationPermission: {
    onChange: (state: PermissionStatus["state"]) => Promise<void>;
    onError?: () => Promise<void>;
  } = {
    onChange: async () => {},
    onError: async () => {},
  };

  loadingGeolocalization = false;

  protected async geolocalizationSuccess() {}
  protected async geolocalizationError() {}

  async beforeMount() {
    this.watchGeolocalizationPermission();
  }

  async destroyed() {
    await this.clearWatchGeolocalizationPermission();
  }

  private async clearWatchGeolocalizationPermission() {
    if (!this.geolocalizationPermissionStatus) return;
    this.geolocalizationPermissionStatus.onchange = () => {};
  }

  async watchGeolocalizationPermission() {
    if (!navigator.permissions) return;
    try {
      // Verifica o estado inicial da permissão
      this.geolocalizationPermissionStatus = await navigator.permissions.query({
        name: "geolocation",
      });

      // Apenas configura o listener para mudanças futuras, não executa imediatamente
      this.geolocalizationPermissionStatus.onchange = () =>
        this.onChangeGeolocalizationPermission.onChange(this.geolocalizationPermissionStatus.state);
    } catch (e) {
      this.onChangeGeolocalizationPermission.onError &&
        (await this.onChangeGeolocalizationPermission.onError());
    }
  }

  async checkInitialGeolocationStatus() {
    if (!navigator.permissions) return;
    try {
      const permissionStatus = await navigator.permissions.query({
        name: "geolocation",
      });

      if (permissionStatus.state === "granted") {
        await this.geolocalizationSuccess();
      } else if (permissionStatus.state === "denied") {
        await this.geolocalizationError();
      }
    } catch (e) {
      await this.geolocalizationError();
    }
  }

  showGelocalizationDialogSuccess() {
    this.$bus.$emit("openDialog", {
      type: "success",
      title: "Geolocalizalização permitida com sucesso!",
      message:
        "Obrigado por permitir o acesso à sua localização! Agora, podemos continuar com a sua proposta de forma segura e personalizada. \n" +
        "Para isso, clique no <b>BOTÃO CONTINUAR<b>",
    });
  }

  showGeolocalizationDialogError() {
    this.$bus.$emit("openDialog", {
      type: "error",
      title: "Não Foi Possível Prosseguir com Sua Proposta",
      message:
        "Parece que o acesso à sua geolocalização não foi permitido. " +
        "Infelizmente, não podemos dar continuidade à sua proposta sem essa autorização. <br><br>" +
        "Para seguir adiante, por favor, <a target='_blank' href='https://support.google.com/chrome/answer/142065?hl=pt-BR&co=GENIE.Platform%3DDesktop'>clique aqui</a> " +
        "para conferir as instruções sobre como ativar a geolocalização.",
    });
  }

  protected async onAcceptGeolocalization() {
    this.loadingGeolocalization = true;
    const { coords } = await Geolocation.getCurrentPosition();
    this.loadingGeolocalization = false;

    if (!hasData(coords?.latitude) && !hasData(coords?.longitude)) throw new Error();

    await this.$onboardApi.resources.plugins.sendPluginData("access_log", {
      lat: coords?.latitude,
      long: coords?.longitude,
      type: "plugin",
    });
  }

  async sendGeolocationData(
    onSend: (geolocalizationData: {
      coords: {
        latitude: number;
        longitude: number;
      };
      error: boolean;
      message: any;
      code: string;
    }) => Promise<ResourceReturnModel<any>>
  ) {
    const geolocalizationData = await Geolocation.getCurrentPosition();

    const { responseObject, error } = await onSend(geolocalizationData);

    if (responseObject.status === 461) {
      this.showGeolocalizationDialogError();
      return false;
    }

    if (error) throw new OnboardApiError(responseObject);

    return true;
  }
}
